import { useEffect, useState, useCallback } from "react";
import {
  Box, Sheet, Typography, Input, Button, Grid, Card,
  LinearProgress, CircularProgress, Alert, Divider,
  Stack,
} from "@mui/joy";
import { motion } from "framer-motion";
import { adminApi } from "../adminApi";
import FunnelChart from "../components/FunnelChart";
import MediaBar from "../components/MediaBar";

const fmt = (n) => (n ?? 0).toLocaleString("ru-RU");
const money = (n) => `${(Number(n) || 0).toLocaleString("ru-RU")} ₽`;
const isoDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};
const todayIso = () => new Date().toISOString().slice(0, 10);

// Обёртка-«всплывание» для блока со сдвигом по индексу (stagger).
function Reveal({ index = 0, children, style }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut", delay: 0.05 * index }}
      style={{ height: "100%", ...style }}
    >
      {children}
    </motion.div>
  );
}

export default function DashboardPage() {
  const [dateFrom, setDateFrom] = useState(isoDaysAgo(30));
  const [dateTo, setDateTo] = useState(todayIso());
  const [funnel, setFunnel] = useState(null);
  const [overview, setOverview] = useState(null);
  const [clientsGeo, setClientsGeo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [f, o, c] = await Promise.all([
        adminApi.funnel(dateFrom, dateTo),
        adminApi.overview(),
        adminApi.dashboardClients(dateFrom, dateTo),
      ]);
      setFunnel(f);
      setOverview(o);
      setClientsGeo(c);
    } catch (e) {
      setError(e.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Карточки overview (кроме «Медиа» — она особая, со шкалой).
  // Ряд 1 — «Медиа» и «Топ городов» (по 6). Ряд 2 — четыре ровные плашки (по 3).
  const cards = overview ? [
    { key: "media", md: 6, node: (
      <Card variant="soft" sx={{ height: "100%" }}>
        <Typography level="title-md" sx={{ fontWeight: 700, mb: 0.5 }}>Медиа (файлы)</Typography>
        <MediaBar media={overview.media} />
      </Card>
    ) },
    { key: "cities", md: 6, node: (
      <Card variant="soft" sx={{ height: "100%" }}>
        <Box sx={{ gap: 1, mb: 0.5 }}>
          <Typography level="title-md" sx={{ fontWeight: 700 }}>Топ городов по заказам</Typography>
          <Typography level="body-xs" color="neutral" sx={{ ml: "auto" }}>всего городов: {fmt(clientsGeo?.cities_count)}</Typography>
        </Box>
        {(clientsGeo?.top_cities || []).length === 0 ? (
          <Typography level="body-xs" color="neutral">Нет данных за период</Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            {clientsGeo.top_cities.map((c, idx) => (
              <Box key={c.city} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography level="body-sm" noWrap>{idx + 1}. {c.city}</Typography>
                <Typography level="body-sm" sx={{ fontWeight: 600 }}>{fmt(c.count)}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Card>
    ) },
    { key: "disk", md: 3, node: (
      <Card variant="soft" sx={{ height: "100%", justifyContent: "space-between", gap: 0.5 }}>
        <Typography level="title-md" sx={{ fontWeight: 700 }}>Диск</Typography>
        {overview.disk?.error ? (
          <Typography level="body-xs" color="danger">{overview.disk.error}</Typography>
        ) : (
          <Box>
            <Typography level="h3">{overview.disk?.used_pct}%</Typography>
            <LinearProgress determinate variant="solid" sx={{ my: 0.5, maxHeight: "min-content" }} value={overview.disk?.used_pct || 0} />
            <Typography level="body-xs" color="neutral">{overview.disk?.used_human} из {overview.disk?.total_human} (свободно {overview.disk?.free_human})</Typography>
          </Box>
        )}
      </Card>
    ) },
    { key: "books", md: 3, node: (
      <Card variant="soft" sx={{ height: "100%", justifyContent: "space-between", gap: 0.5 }}>
        <Typography level="title-md" sx={{ fontWeight: 700 }}>Книги</Typography>
        <Box>
          <Typography level="h3">{fmt(overview.books?.total)}</Typography>
          <Typography level="body-xs" color="neutral">в корзине: {fmt(overview.books?.in_cart)}<br />медиа очищено: {fmt(overview.books?.media_cleaned)}</Typography>
        </Box>
      </Card>
    ) },
    { key: "orders", md: 3, node: (
      <Card variant="soft" sx={{ height: "100%", justifyContent: "space-between", gap: 0.5 }}>
        <Typography level="title-md" sx={{ fontWeight: 700 }}>Заказы</Typography>
        <Box>
          <Typography level="h3">{fmt(overview.orders?.total)}</Typography>
          <Typography level="body-xs" color="neutral">оплачено: {fmt(overview.orders?.by_status?.paid)}{" · "}выполнено: {fmt(overview.orders?.by_status?.done)}</Typography>
        </Box>
      </Card>
    ) },
    { key: "clients", md: 3, node: (
      <Card variant="soft" sx={{ height: "100%", justifyContent: "space-between", gap: 0.5 }}>
        <Typography level="title-md" sx={{ fontWeight: 700 }}>Клиенты</Typography>
        <Box>
          <Typography level="h3">{fmt(clientsGeo?.clients_total)}</Typography>
          <Typography level="body-xs" color="neutral">~{fmt(clientsGeo?.avg_new_clients_per_day)} новых/день<br />+{fmt(clientsGeo?.new_clients_in_range)} за период</Typography>
        </Box>
      </Card>
    ) },
  ] : [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Reveal index={0}>
        <Stack direction={"row"} sx={{ gap: 2, flexWrap: "wrap", alignItems: 'flex-end' }}>
          <Typography level="h3" sx={{ mr: "auto" }}>Дашборд</Typography>
          <Stack direction={'row'} sx={{ gap: 2, alignItems: 'flex-end', flexWrap: "wrap" }}>
            <Stack direction={'row'} sx={{ gap: 1, alignItems: 'flex-end' }}>
              <Typography level="body-xs" color="neutral">С</Typography>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </Stack>
            <Stack direction={'row'} sx={{ gap: 1, alignItems: 'flex-end' }}>
              <Typography level="body-xs" color="neutral">По</Typography>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </Stack>
            <Button onClick={load} loading={loading} sx={{ height: 'fit-content' }}>Обновить</Button>
          </Stack>
        </Stack>
      </Reveal>

      {error && <Alert color="danger" variant="soft">{error}</Alert>}

      {overview && (
        <Grid container spacing={2}>
          {cards.map((c, i) => (
            <Grid key={c.key} xs={12} sm={6} md={c.key === "media" ? 6 : 3} sx={{ height: "fit-content" }}>
              <Reveal index={i + 1}>{c.node}</Reveal>
            </Grid>
          ))}
        </Grid>
      )}

      <Reveal index={5}>
        <Sheet variant="outlined" sx={{ p: 3, borderRadius: "14px" }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Typography level="title-lg">Воронка</Typography>
            {funnel?.range && (
              <Typography level="body-sm" color="neutral">
                {funnel.range.date_from} — {funnel.range.date_to}
              </Typography>
            )}
            <Box sx={{ ml: "auto", display: "flex", gap: 3 }}>
              <Box sx={{ textAlign: "right" }}>
                <Typography level="body-xs" color="neutral">Выручка получена</Typography>
                <Typography level="title-md" color="success">{money(funnel?.totals?.revenue_received)}</Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography level="body-xs" color="neutral">Ожидается</Typography>
                <Typography level="title-md">{money(funnel?.totals?.revenue_pending)}</Typography>
              </Box>
            </Box>
          </Box>

          {loading && !funnel ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
          ) : (
            <FunnelChart stages={funnel?.stages} />
          )}

          <Divider sx={{ my: 2 }} />
          <Typography level="body-xs" color="neutral">
            Стадии «Создано / больше 50% / 100%» считаются по книгам, «Заказ / Оплата / Генерация / Получено» — по заказам
            (смена единицы счёта).
          </Typography>
        </Sheet>
      </Reveal>
    </Box>
  );
}